/**
 * Pickup times are stored as the wall-clock time the customer asked for,
 * labelled UTC: a 10:00 pickup is saved as "...T10:00:00.000Z". Bulk uploads
 * are stored this way and every screen that shows a pickup (booking list and
 * view, dashboard, invoices, the WhatsApp/SMS/email texts) reads it back with
 * UTC getters, so the booking form writes and reads it the same way. Building
 * it from the browser's local clock instead shifted manual bookings by the
 * IST offset (a 10:00 pickup showed as 04:30).
 */

const pad = (n) => String(n).padStart(2, "0");

/** "2026-10-01" + "10:00" -> "2026-10-01T10:00:00.000Z"; null when invalid. */
export function toPickupAtIso(dateStr, timeStr) {
  const date = String(dateStr || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!date) return null;

  let hour = 0;
  let minute = 0;
  const time = String(timeStr || "").trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (time) {
    hour = Number(time[1]);
    minute = Number(time[2]);
  }
  if (hour > 23 || minute > 59) return null;

  const [, y, m, d] = date.map(Number);
  const at = new Date(Date.UTC(y, m - 1, d, hour, minute));
  // Date.UTC rolls 2026-02-31 over into March; treat that as invalid.
  if (at.getUTCMonth() !== m - 1 || at.getUTCDate() !== d) return null;
  return at.toISOString();
}

/** Inverse of toPickupAtIso: a stored pickupAt -> { date, time } for the form. */
export function splitPickupAt(value) {
  const at = value ? new Date(value) : null;
  if (!at || Number.isNaN(at.getTime())) return { date: "", time: "" };
  return {
    date: `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`,
    time: `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`,
  };
}

/* The person booking types the time on their own clock, so "is it in the
   past?" compares against the browser's local date and time, never UTC. */

export const localToday = (now = new Date()) =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

export const localTimeNow = (now = new Date()) => `${pad(now.getHours())}:${pad(now.getMinutes())}`;

export function isPickupInPast(dateStr, timeStr, now = new Date()) {
  if (!dateStr) return false;
  const today = localToday(now);
  if (dateStr !== today) return dateStr < today;
  return Boolean(timeStr) && String(timeStr).slice(0, 5) < localTimeNow(now);
}
